import React, { ChangeEvent, useEffect, useRef, useState } from "react";
import { useCallback } from "react";
import { CreateIcon, EditIcon } from "assets/icons";
import ConfirmationModal from "features/ConfirmationModal";
import Guard from "features/Guard";
import {
  useAppDispatch,
  useAppSelector,
  useDebounce,
  useLocalStorage,
  useSortColumn,
} from "hooks";
import i18n from "i18n";
import { IConfirmationModal, IUser } from "models/user";
import { useTranslation } from "react-i18next";
import {
  blockUser,
  getAllowedGroupsByMenuId,
  getAllowedPermissionsByGroupId,
  getCountries,
  getLookupByName,
  getUserRoles,
  getUsers,
  setSearchUser,
  unblockUser,
} from "store";
import {
  createOrEditBtn,
  resetDefaultSelectBackground,
  selectWithCloseIcon,
} from "styles/MUIStyles";
import { sendNotification } from "ui/Toast";

import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import {
  Autocomplete,
  Checkbox,
  IconButton,
  InputLabel,
  Paper,
  TextField,
} from "@mui/material";
import Backdrop from "@mui/material/Backdrop";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import FormControl from "@mui/material/FormControl";
import MenuItem from "@mui/material/MenuItem";
import Pagination from "@mui/material/Pagination";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import Stack from "@mui/material/Stack";

import CreateUserForm from "./CreateUserForm";
import UpdateUserForm from "./UpdateUserForm";
import UsersTable from "./UsersTable";

import usersStyles from "./Users.module.scss";

const Users = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const allowedGroups = useAppSelector((state) => state.auth.allowedGroups);
  const allowedMenus = useAppSelector((state) => state.auth.allowedMenus);
  const allowedPermissions = useAppSelector(
    (state) => state.auth.allowedPermissions,
  );
  const searchUser = useAppSelector((state) => state.users.searchUser);
  const totalCount = useAppSelector((state) => state.users.totalCount);
  const isUsersLoading = useAppSelector((state) => state.users.isUsersLoading);

  const isUserSearchingRef = useRef<boolean>(false);
  const [activeUser, setActiveUser] = useState<null | IUser>(null);
  const [pageSize, setPageSize] = useLocalStorage<number>("usersPageSize", 10);
  const [pageNumber, setPageNumber] = useLocalStorage("usersPageNumber", 1);
  const [roleNames, setRoleNames] = useState<string[]>([]);
  const [createOrUpdate, setCreateOrUpdate] = useState<null | string>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
  const [userStatus, setUserStatus] = useLocalStorage("userStatus", "");
  const debouncedSearchUserName = useDebounce(searchUser.userName);
  const debouncedSearchUserEmail = useDebounce(searchUser.email);
  const { sortConfig, handleColumnClick } = useSortColumn(
    "usersTableSortConfig",
  );
  const roles = useAppSelector((state) => state.lookups.roles);
  const [confirmationModalOpen, setConfirmationModalOpen] =
    useState<IConfirmationModal>({
      isOpen: false,
      actionName: "",
    });

  const getRolesTranslated = useCallback(
    (roles: string[]) => roles.map((role) => t(`roles.${role}`) ?? role),
    [t, i18n.language],
  );

  const activateItem = async () => {
    if (activeUser?.id) {
      const response = await dispatch(unblockUser(activeUser.id));

      if (response.meta.requestStatus === "fulfilled") {
        sendNotification(t("notifications.successfullyUnblocked"), "success");
        await dispatch(
          getUsers({
            userName: debouncedSearchUserName,
            email: debouncedSearchUserEmail,
            pageNumber,
            userStatus,
            pageSize,
            sortBy: sortConfig,
          }),
        );
        setConfirmationModalOpen({ isOpen: false, actionName: "" });
      }
    }
  };

  const deactivateItem = async () => {
    if (activeUser?.id) {
      try {
        const response = await dispatch(blockUser(activeUser.id));

        if (response.meta.requestStatus === "fulfilled") {
          sendNotification(t("notifications.successfullyBlocked"), "success");
          await dispatch(
            getUsers({
              userName: debouncedSearchUserName,
              email: debouncedSearchUserEmail,
              pageNumber,
              userStatus,
              pageSize,
              sortBy: sortConfig,
            }),
          );
        } else {
          const errorMessage =
            response.payload?.error || t("notifications.userWithEmailNotFound");
          sendNotification(errorMessage, "error");
        }
      } catch (error) {
        console.log(error);
        sendNotification(t("notifications.failedToBlock"), "error");
      } finally {
        setConfirmationModalOpen({ isOpen: false, actionName: "" });
      }
    }
  };

  const confirmActionHandler = () => {
    switch (confirmationModalOpen.actionName) {
      case "Unblock":
        activateItem();
        break;
      case "Block":
        deactivateItem();
        break;
    }
  };

  const onCreateUser = () => {
    setIsDrawerOpen(true);
    setCreateOrUpdate("create");
  };

  const handleUserStatusChange = (event: SelectChangeEvent) => {
    setUserStatus(event.target.value as string);
    setPageNumber(1);
  };

  const handleSearchUser = (e: any) => {
    isUserSearchingRef.current = true;
    const { name, value } = e.target;
    dispatch(setSearchUser({ name, value }));
  };

  const handleOnPageChange = (event: ChangeEvent<unknown>, page: number) => {
    setActiveUser(null);
    setPageNumber(page);
  };

  const onEditUser = () => {
    setIsDrawerOpen(true);
    setCreateOrUpdate("update");
  };

  const handlePageSizeChange = (event: SelectChangeEvent) => {
    setPageSize(event.target.value);
    setPageNumber(1);
    dispatch(
      getUsers({
        userName: searchUser.userName,
        email: searchUser.email,
        pageNumber: 1,
        userStatus,
        pageSize: +event.target.value,
        sortBy: sortConfig,
      }),
    );
  };

  useEffect(() => {
    const menu = allowedMenus.find((menu) => menu.name === "Users");
    typeof menu?.id === "number" && dispatch(getAllowedGroupsByMenuId(menu.id));
  }, [dispatch, allowedMenus]);

  useEffect(() => {
    const group = allowedGroups.find((menu) => menu.name === "api/users");
    group?.id && dispatch(getAllowedPermissionsByGroupId(group.id));
  }, [dispatch, allowedGroups]);

  useEffect(() => {
    dispatch(getCountries("countries"));
    dispatch(getUserRoles());
  }, [dispatch, t]);

  useEffect(() => {
    dispatch(getLookupByName("roles"));
  }, []);

  const handleUserRoleChange = (event: SelectChangeEvent<string[]>) => {
    setRoleNames(event.target.value as string[]);
  };

  return (
    <div>
      <div className={usersStyles.crudButtonWrapper}>
        <Guard allowedPermissions={allowedPermissions} permissionName="Create">
          <Button
            startIcon={<CreateIcon />}
            variant="text"
            disabled={false}
            onClick={() => onCreateUser()}
            sx={createOrEditBtn}
          >
            {t("buttons.Create")}
          </Button>
        </Guard>
        <Guard allowedPermissions={allowedPermissions} permissionName="Edit">
          <Button
            startIcon={<EditIcon />}
            variant="text"
            disabled={!activeUser}
            onClick={() => onEditUser()}
            sx={createOrEditBtn}
          >
            {t("buttons.Edit")}
          </Button>
        </Guard>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: ".7rem",
            marginLeft: "auto",
            marginRight: "2rem",
          }}
        >
          <span>{t("filters.elementsOnPage")}</span>
          <FormControl variant="standard">
            <Select
              labelId="demo-simple-select-standard-label"
              id="demo-simple-select-standard"
              value={pageSize}
              onChange={handlePageSizeChange}
            >
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={25}>25</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </div>
      <div className={usersStyles.usersFiltersWrapper}>
        <div className={usersStyles.searchWrapper}>
          <input
            type="text"
            name="userName"
            autoComplete="off"
            value={searchUser.userName}
            onChange={handleSearchUser}
            placeholder={t("filters.searchByNameAndSurname") as string}
            className={usersStyles.search}
          />
          <button type="button" className={usersStyles.searchIconWrapper}>
            <SearchIcon sx={{ color: "#fff" }} />
          </button>
        </div>
        <div className={usersStyles.searchWrapper}>
          <input
            type="text"
            name="email"
            autoComplete="off"
            value={searchUser.email}
            onChange={handleSearchUser}
            placeholder={t("filters.searchByEmail") as string}
            className={usersStyles.search}
          />
          <button type="button" className={usersStyles.searchIconWrapper}>
            <SearchIcon sx={{ color: "#fff" }} />
          </button>
        </div>
        <Box sx={{ flexBasis: "20%", minWidth: 250 }}>
          <Autocomplete
            multiple
            disableCloseOnSelect
            options={roles}
            value={roleNames}
            onChange={(e, newValue) => setRoleNames(newValue)}
            getOptionLabel={(option) => t(`roles.${option}`)}
            renderOption={(props, option, { selected }) => (
              <li {...props} style={{ height: 48 }}>
                <Checkbox
                  style={{ marginRight: 8 }}
                  checked={selected}
                  tabIndex={-1}
                  disableRipple
                />
                {t(`roles.${option}`)}
              </li>
            )}
            renderTags={() => null}
            ListboxProps={{
              style: {
                maxHeight: 240,
                overflowY: "auto",
                padding: 0,
                margin: 0,
              },
            }}
            renderInput={(params) => {
              const selectedText = roleNames
                .map((v) => t(`roles.${v}`))
                .join(", ");
              const showPlaceholder = roleNames.length === 0;

              return (
                <TextField
                  {...params}
                  variant="outlined"
                  placeholder={String(t("filters.chooseRoleName"))}
                  inputProps={{
                    ...params.inputProps,
                    readOnly: true,
                    value: showPlaceholder ? "" : selectedText,
                    style: {
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      padding: 0,
                      margin: 0,
                      lineHeight: "41px",
                    },
                  }}
                  InputProps={{
                    ...params.InputProps,
                    sx: {
                      height: "41px",
                      borderRadius: 0,
                      paddingRight: "40px",
                      display: "flex",
                      alignItems: "center",
                      "& fieldset": {
                        borderColor: "#9b9b9b",
                      },
                      "&:hover fieldset": {
                        borderColor: "#9b9b9b",
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#80bdff",
                        borderWidth: 2,
                      },
                    },
                  }}
                />
              );
            }}
            PaperComponent={(props) => (
              <Paper
                {...props}
                sx={{
                  maxHeight: 240,
                  minHeight: 240,
                  overflow: "visible",
                  py: 0,
                  px: 0,
                  "&::-webkit-scrollbar": {
                    width: "6px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "#C0D4E3",
                    borderRadius: "4px",
                  },
                  "&::-webkit-scrollbar-track": {
                    backgroundColor: "transparent",
                  },
                }}
              />
            )}
          />
        </Box>
        <Box sx={{ flexBasis: "20%", minWidth: 250 }}>
          <Autocomplete
            disableClearable
            options={[
              { id: 1, name: t("filters.active") },
              { id: 2, name: t("filters.inactive") },
              { id: 3, name: t("filters.markedForDeletion") },
            ]}
            value={
              [
                { id: 1, name: t("filters.active") },
                { id: 2, name: t("filters.inactive") },
                { id: 3, name: t("filters.markedForDeletion") },
              ].find((option) => option.id === userStatus) || undefined
            }
            onChange={(_, newValue) => setUserStatus(newValue?.id ?? "")}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            getOptionLabel={(option) => option.name || ""}
            renderOption={(props, option) => (
              <span {...props} className={usersStyles.dropDownItem}>
                {option.name}
              </span>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={t("filters.selectStatus") as string}
                size="small"
                variant="outlined"
                sx={{
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderRadius: "0",
                    border: "1px solid #8A8886",
                  },
                  "& .MuiOutlinedInput-root": {
                    "&:hover fieldset": {
                      border: "1px solid #8A8886",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#80bdff",
                      boxShadow: "0 0 0 0.2rem rgba(0, 123, 255, .25)",
                    },
                  },
                  padding: "5px 0",
                }}
              />
            )}
          />
        </Box>
      </div>
      <UsersTable
        sortConfig={sortConfig}
        activeUser={activeUser}
        setActiveUser={setActiveUser}
        onEditUser={onEditUser}
        handleColumnClick={handleColumnClick}
        setConfirmationModalOpen={setConfirmationModalOpen}
        isUserSearchingRef={isUserSearchingRef}
        pageNumber={pageNumber}
        roleNames={roleNames}
        setPageNumber={setPageNumber}
        debouncedSearchUserName={debouncedSearchUserName}
        debouncedSearchUserEmail={debouncedSearchUserEmail}
        userStatus={userStatus}
        pageSize={pageSize}
      />
      <Stack sx={{ width: "500px", margin: "12px auto 0 auto" }}>
        <Pagination
          sx={{ margin: "auto" }}
          count={totalCount ? Math.ceil(totalCount / Number(pageSize)) : 1}
          page={pageNumber}
          size="medium"
          onChange={handleOnPageChange}
          shape="rounded"
          color="primary"
          variant="outlined"
        />
      </Stack>
      <Backdrop
        sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={isUsersLoading}
      >
        <CircularProgress sx={{ color: "#fff" }} />
      </Backdrop>
      {createOrUpdate === "create" ? (
        <CreateUserForm
          sortConfig={sortConfig}
          createOrUpdate={createOrUpdate}
          setIsDrawerOpen={setIsDrawerOpen}
          isDrawerOpen={isDrawerOpen}
          setCreateOrUpdate={setCreateOrUpdate}
          pageNumber={pageNumber}
          pageSize={pageSize}
          debouncedSearchUserName={debouncedSearchUserName}
          debouncedSearchUserEmail={debouncedSearchUserEmail}
          userStatus={userStatus}
        />
      ) : (
        <UpdateUserForm
          sortConfig={sortConfig}
          createOrUpdate={createOrUpdate}
          setIsDrawerOpen={setIsDrawerOpen}
          isDrawerOpen={isDrawerOpen}
          activeUser={activeUser}
          setActiveUser={setActiveUser}
          setCreateOrUpdate={setCreateOrUpdate}
          pageNumber={pageNumber}
          pageSize={pageSize}
          debouncedSearchUserName={debouncedSearchUserName}
          debouncedSearchUserEmail={debouncedSearchUserEmail}
          userStatus={userStatus}
        />
      )}
      <ConfirmationModal
        isModalOpen={confirmationModalOpen.isOpen}
        setIsModalOpen={setConfirmationModalOpen}
        actionName={confirmationModalOpen.actionName}
        instanceName={"users"}
        handleAction={confirmActionHandler}
      />
    </div>
  );
};

export default Users;
